<template>
    <div class="payment-page">
        <HeaderPc />
        <div class="payment-main">
            <div class="container">
                <!-- 支付进度提示 -->
                <div class="payment-step">
                    <div class="step-item" :class="{ active: ['pending', 'processing', 'success', 'failed'].includes(paymentStatus) }">
                        <span class="step-icon">1</span>
                        <span class="step-text">确认订单</span>
                    </div>
                    <div class="step-line" :class="{ active: ['processing', 'success', 'failed'].includes(paymentStatus) }"></div>
                    <div class="step-item" :class="{ active: ['processing', 'success', 'failed'].includes(paymentStatus) }">
                        <span class="step-icon">2</span>
                        <span class="step-text">完成支付</span>
                    </div>
                    <div class="step-line" :class="{ active: ['success'].includes(paymentStatus) }"></div>
                    <div class="step-item" :class="{ active: ['success'].includes(paymentStatus) }">
                        <span class="step-icon" v-if="paymentStatus === 'success'">✓</span>
                        <span class="step-icon" v-else>3</span>
                        <span class="step-text">支付成功</span>
                    </div>
                </div>

                <div class="payment-content" v-if="courseInfo.course">
                    <!-- 课程信息卡片 -->
                    <div class="course-card">
                        <div class="card-header">
                            <h3 class="card-title">课程信息</h3>
                        </div>
                        <div class="card-body">
                            <div class="course-info-wrap">
                                <div class="course-cover">
                                    <img :src="getImageUrl(courseInfo.course.cover)" alt="课程封面" @error="handleCoverError($event)">
                                </div>
                                <div class="course-detail">
                                    <h4 class="course-name">{{ courseInfo.course.name || '' }}</h4>
                                    <div class="course-meta">
                                        <span class="meta-item">
                                            <i class="el-icon-user-solid"></i>
                                            讲师：{{ courseInfo.author.name || '' }}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 支付核心区域 -->
                    <div class="payment-card">
                        <!-- 待支付状态（第一步） -->
                        <div v-if="paymentStatus === 'pending'">
                            <div class="card-header">
                                <h3 class="card-title">确认支付</h3>
                                <div class="countdown-tip">
                                    <i class="el-icon-clock"></i>
                                    支付剩余时间：<span class="countdown">{{ countdownText || '' }}</span>
                                </div>
                            </div>
                            
                            <div class="card-body">
                                <!-- 支付金额 -->
                                <div class="payment-amount">
                                    <span class="amount-label">应付金额：</span>
                                    <span class="amount-value">¥{{ parseFloat(courseInfo.course.price).toFixed(2) }}</span>
                                </div>

                                <!-- 支付二维码 -->
                                <div class="qrcode-wrap">
                                    <div class="qrcode-tip">
                                        <p>请使用{{ getPayMethodName() }}扫码支付</p>
                                        <p>支付金额：¥{{ parseFloat(courseInfo.course.price).toFixed(2) }}</p>
                                    </div>
                                    <div class="qrcode-img">
                                        <img :src="qrcodeUrl" alt="支付二维码">
                                    </div>
                                    <div class="qrcode-notice">
                                        <i class="el-icon-info"></i>
                                        扫码后请等待页面自动跳转，请勿刷新页面
                                    </div>
                                </div>

                                <!-- 支付操作按钮 -->
                                <div class="payment-actions">
                                    <el-button @click="goBack" class="cancel-btn">取消支付</el-button>
                                    <el-button type="primary" @click="checkPayStatus" class="check-btn">
                                        <i class="el-icon-refresh"></i>
                                        已支付，刷新状态
                                    </el-button>
                                </div>
                            </div>
                        </div>

                        <!-- 支付中状态（第二步） -->
                        <div v-if="paymentStatus === 'processing'">
                            <div class="card-header">
                                <h3 class="card-title">支付确认中</h3>
                            </div>
                            
                            <div class="card-body">
                                <div class="processing-wrap">
                                    <div class="loading-icon">
                                        <i class="el-icon-loading"></i>
                                    </div>
                                    <div class="processing-text">
                                        <h4>正在确认您的支付信息</h4>
                                        <p>请稍候，系统正在验证支付结果...</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 支付成功状态（第三步） -->
                        <div v-if="paymentStatus === 'success'">
                            <div class="card-header">
                                <h3 class="card-title">支付成功</h3>
                            </div>
                            
                            <div class="card-body">
                                <div class="success-wrap">
                                    <div class="success-icon">
                                        <i class="el-icon-circle-check"></i>
                                    </div>
                                    <div class="success-text">
                                        <h4>恭喜！支付成功</h4>
                                        <p>您已成功购买《{{ courseInfo.course.name }}》课程</p>
                                        <p class="order-number">订单编号：<strong>{{ courseInfo.payinfo.order_no }}</strong></p>
                                    </div>
                                    <div class="success-actions">
                                        <el-button type="primary" @click="goToCourse" class="go-course-btn">
                                            <i class="el-icon-video-play"></i>
                                            立即学习
                                        </el-button>
                                        <el-button @click="goBackHome" class="go-home-btn">
                                            <i class="el-icon-s-home"></i>
                                            返回首页
                                        </el-button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 支付失败状态 -->
                        <div v-if="paymentStatus === 'failed'">
                            <div class="card-header">
                                <h3 class="card-title">支付失败</h3>
                            </div>
                            
                            <div class="card-body">
                                <div class="failed-wrap">
                                    <div class="failed-icon">
                                        <i class="el-icon-circle-close"></i>
                                    </div>
                                    <div class="failed-text">
                                        <h4>支付失败，请重试</h4>
                                        <p>支付金额：¥{{ parseFloat(courseInfo.course.price).toFixed(2) }}</p>
                                        <p>请检查支付方式或重新扫码支付</p>
                                    </div>
                                    <div class="failed-actions">
                                        <el-button type="primary" @click="retryPay" class="retry-btn">
                                            <i class="el-icon-refresh"></i>
                                            重新支付
                                        </el-button>
                                        <el-button @click="contactService" class="service-btn">
                                            <i class="el-icon-phone"></i>
                                            联系客服
                                        </el-button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <FooterPc />
    </div>
</template>

<script>
import HeaderPc from '@/components/pc/header.vue'
import FooterPc from '@/components/pc/fotter.vue'
import { getCoursePayInfo, getPayStatus } from '@/api/home'
import { Session } from '@/utils/storage'

export default {
    name: 'PaymentPage',
    components: {
        HeaderPc,
        FooterPc
    },
    data() {
        return {
            courseInfo: {},
            selectedPayMethod: 'wechat',
            qrcodeUrl: '',
            countdown_time: 5 * 60,
            countdown: 5 * 60,
            countdownText: '',
            countdownTimer: null,
            paymentStatus: 'pending',
            checkPayStatusTimer: null,
            orderNumber: 'PAY' + Date.now(),
            checkCount: 0
        }
    },
    created() {
        if (!Session.get('token')) {
            this.$message.warning('请先登录')
            this.$router.push('/').catch(() => {})
            return
        }
        
        const courseId = this.$route.query.course_id
        if (!courseId) {
            this.$message.error('课程信息不存在')
            this.$router.push('/').catch(() => {})
            return
        }
        
        this.fetchCourseInfo(courseId)
    },
    beforeDestroy() {
        clearInterval(this.countdownTimer)
        clearInterval(this.checkPayStatusTimer)
    },
    methods: {
        fetchCourseInfo(id) {
            getCoursePayInfo({id}).then(res => {
                if (res.code && res.data) {
                    this.courseInfo = res.data
                    this.qrcodeUrl = process.env.VUE_APP_STATIC_URL + res.data.qrcode_image || ''
                    this.countdown_time = res.data.timeout || 5 * 60
                    this.countdown = res.data.timeout || 5 * 60

                    this.initCountdown()
                    this.checkPayStatusTimer = setInterval(() => {
                        this.checkPayStatus()
                    }, 5000)
                } else {
                    this.$message.error(res.msg || '课程信息获取失败')
                    this.$router.push('/')
                }
            }).catch((err) => {
                this.$message.error(err.msg || '课程信息获取失败')
                this.$router.push('/').catch(() => {})
            })
        },
        // 初始化倒计时
        initCountdown() {
            this.updateCountdownText()
            this.countdownTimer = setInterval(() => {
                if (this.countdown <= 0) {
                    clearInterval(this.countdownTimer)
                    this.paymentStatus = 'failed'
                    this.$message.warning('支付超时，请重新下单')
                    return
                }
                this.countdown--
                this.updateCountdownText()
            }, 1000)
        },
        // 更新倒计时文本
        updateCountdownText() {
            const minutes = Math.floor(this.countdown / 60)
            const seconds = this.countdown % 60
            this.countdownText = `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
        },
        // 获取支付方式名称
        getPayMethodName() {
            return '微信支付'
        },
        handleCoverError(e) {
            e.target.src = require('@/static/imgs/default.jpeg')
        },
        // 返回上一页
        goBack() {
            this.$router.go(-1)
        },
        // 检查支付状态（模拟接口逻辑）
        checkPayStatus() {
            if (this.paymentStatus !== 'pending') return
            
            getPayStatus({order_no: this.courseInfo.payinfo.order_no}).then(res => { 
                if (res.code && res.data) {
                    if (res.data.status === 'succ') {
                        this.paymentStatus = 'success'
                        this.clearAllTimers()
                        this.$message.success('支付成功！')
                    } else if (res.data.status === 'cancel') {
                        this.paymentStatus = 'failed'
                        this.clearAllTimers()
                        this.$message.warning('支付失败，请重新下单')
                    }
                }
            })
        },
        // 清除所有定时器
        clearAllTimers() {
            clearInterval(this.countdownTimer)
            clearInterval(this.checkPayStatusTimer)
        },
        // 跳转到课程学习页
        goToCourse() {
            let id = this.courseInfo.course.id;
            this.$router.push(`/course/${id}`)
        },
        // 返回首页
        goBackHome() {
            this.$router.push('/')
        },
        // 重新支付
        retryPay() {
            this.paymentStatus = 'pending'
            this.countdown = this.countdown_time
            this.checkCount = 0
            this.initCountdown()
            // 重新启动轮询
            this.checkPayStatusTimer = setInterval(() => {
                this.checkPayStatus()
            }, 5000)
        },
        // 联系客服
        contactService() {
            const findHeaderComponent = (component) => {
                if (component.$options.name === 'HeaderPc') return component
                if (component.$children) {
                    for (let child of component.$children) {
                        const found = findHeaderComponent(child)
                        if (found) return found
                    }
                }
                return null
            }
            
            const headerComponent = findHeaderComponent(this.$root)
            if (headerComponent && headerComponent.handleShowService) {
                headerComponent.handleShowService()
            } else {
                this.$message.info('已联系客服，客服会尽快处理')
            }
        },
        getImageUrl(url) {
            if (!url) return require('@/static/imgs/default.jpeg')
            if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) return url
            return process.env.VUE_APP_STATIC_URL + url
        }
    }
}
</script>

<style scoped>
.payment-page {
    min-height: 100vh;
    background-color: #f5f7fa;
    font-size: 14px;
}
.container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 20px;
    box-sizing: border-box;
}

.payment-main {
    padding: 40px 0;
}
/* 支付步骤 */
.payment-step {
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 40px;
}
.step-item {
    display: flex;
    flex-direction: column;
    align-items: center;
    position: relative;
}
.step-icon {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: #e5e6eb;
    color: #999;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    margin-bottom: 8px;
    transition: all 0.3s ease;
}
.step-text {
    font-size: 14px;
    color: #999;
}
.step-item.active .step-icon {
    background: linear-gradient(135deg, #1890ff, #40a9ff);
    color: #fff;
    box-shadow: 0 4px 12px rgba(24, 144, 255, 0.3);
}
.step-item.active .step-text {
    color: #1890ff;
    font-weight: 500;
}
.step-line {
    width: 80px;
    height: 2px;
    background: #e5e6eb;
    margin: 0 16px;
    transition: all 0.3s ease;
}
.step-line.active {
    background: linear-gradient(135deg, #1890ff, #40a9ff);
}

/* 支付内容布局 */
.payment-content {
    display: flex;
    gap: 30px;
}
.course-card {
    flex: 1;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}
.payment-card {
    width: 420px;
    background: #fff;
    border-radius: 16px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.05);
    overflow: hidden;
    flex-shrink: 0;
}

/* 卡片通用样式 */
.card-header {
    padding: 24px;
    border-bottom: 1px solid #f5f7fa;
    display: flex;
    justify-content: space-between;
    align-items: center;
}
.card-title {
    font-size: 18px;
    font-weight: 600;
    color: #333;
}
.card-body {
    padding: 24px;
}

/* 课程信息卡片 */
.course-info-wrap {
    display: flex;
    gap: 20px;
}
.course-cover {
    width: 120px;
    height: 80px;
    border-radius: 12px;
    overflow: hidden;
    flex-shrink: 0;
}
.course-cover img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.course-name {
    font-size: 16px;
    font-weight: 500;
    color: #333;
    margin-bottom: 12px;
    line-height: 1.4;
}
.course-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
}
.meta-item {
    font-size: 12px;
    color: #666;
    display: flex;
    align-items: center;
    gap: 4px;
}

/* 支付卡片样式 */
.countdown-tip {
    font-size: 12px;
    color: #ff4d4f;
    display: flex;
    align-items: center;
    gap: 4px;
}
.countdown {
    font-weight: 600;
}

.payment-amount {
    padding: 16px 0;
    border-bottom: 1px solid #f5f7fa;
    margin-bottom: 24px;
}
.amount-label {
    font-size: 14px;
    color: #666;
}
.amount-value {
    font-size: 28px;
    font-weight: 700;
    color: #ff4d4f;
    margin-left: 8px;
}

.qrcode-wrap {
    text-align: center;
    padding: 20px;
    background: #f9f9f9;
    border-radius: 12px;
    margin-bottom: 30px;
}
.qrcode-tip {
    margin-bottom: 16px;
}
.qrcode-tip p {
    font-size: 14px;
    color: #333;
    line-height: 1.6;
}
.qrcode-tip p:last-child {
    color: #ff4d4f;
    font-weight: 500;
}
.qrcode-img {
    width: 200px;
    height: 200px;
    margin: 0 auto 16px;
    border: 1px solid #e5e6eb;
    padding: 12px;
    border-radius: 8px;
}
.qrcode-img img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}
.qrcode-notice {
    font-size: 12px;
    color: #999;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.payment-actions {
    display: flex;
    gap: 16px;
}
.cancel-btn {
    flex: 1;
    height: 48px;
    border-radius: 12px;
    background: #f5f7fa;
    color: #666;
    border: none;
}
.check-btn {
    flex: 2;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1890ff, #40a9ff);
    border: none;
}
.check-btn:hover {
    background: linear-gradient(135deg, #096dd9, #1890ff);
}

/* 支付中样式 */
.processing-wrap {
    text-align: center;
    padding: 40px 0;
}
.loading-icon {
    font-size: 48px;
    color: #1890ff;
    margin-bottom: 16px;
    animation: loading 1.5s linear infinite;
}
@keyframes loading {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}
.processing-text h4 {
    font-size: 18px;
    color: #333;
    margin-bottom: 8px;
}
.processing-text p {
    font-size: 14px;
    color: #666;
}

/* 支付成功样式 */
.success-wrap {
    text-align: center;
    padding: 20px 0;
}
.success-icon {
    font-size: 64px;
    color: #52c41a;
    margin-bottom: 20px;
}
.success-text h4 {
    font-size: 20px;
    color: #333;
    font-weight: 600;
    margin-bottom: 8px;
}
.success-text p {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}
.order-number {
    color: #999;
    font-size: 12px !important;
    margin-top: 8px !important;
}
.success-actions {
    display: flex;
    gap: 16px;
    margin-top: 30px;
}
.go-course-btn {
    flex: 2;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #52c41a, #73d13d);
    border: none;
}
.go-course-btn:hover {
    background: linear-gradient(135deg, #389e0d, #52c41a);
}
.go-home-btn {
    flex: 1;
    height: 48px;
    border-radius: 12px;
    background: #f5f7fa;
    color: #666;
    border: none;
}

/* 支付失败样式 */
.failed-wrap {
    text-align: center;
    padding: 20px 0;
}
.failed-icon {
    font-size: 64px;
    color: #ff4d4f;
    margin-bottom: 20px;
}
.failed-text h4 {
    font-size: 20px;
    color: #ff4d4f;
    font-weight: 600;
    margin-bottom: 8px;
}
.failed-text p {
    font-size: 14px;
    color: #666;
    margin-bottom: 8px;
}
.failed-actions {
    display: flex;
    gap: 16px;
    margin-top: 30px;
}
.retry-btn {
    flex: 2;
    height: 48px;
    border-radius: 12px;
    background: linear-gradient(135deg, #1890ff, #40a9ff);
    border: none;
}
.retry-btn:hover {
    background: linear-gradient(135deg, #096dd9, #1890ff);
}
.service-btn {
    flex: 1;
    height: 48px;
    border-radius: 12px;
    background: #f5f7fa;
    color: #666;
    border: none;
}

/* 响应式适配 */
@media (max-width: 992px) {
    .payment-content {
        flex-direction: column;
    }
    .payment-card {
        width: 100%;
    }
}

@media (max-width: 768px) {
    .payment-step {
        margin-bottom: 30px;
    }
    .step-line {
        width: 40px;
    }
    .course-info-wrap {
        flex-direction: column;
    }
    .course-cover {
        width: 100%;
        height: auto;
    }
    .qrcode-img {
        width: 180px;
        height: 180px;
    }
    .success-actions, .failed-actions {
        flex-direction: column;
    }
}
</style>